// ─── src/components/dashboard/CatalystCheckInScanner.tsx ─────────────────────
//
// Catalyst-scoped door scanner. Same UX as the admin CheckInScanner, but calls
// the /catalyst-portal/* endpoints and uses the catalyst attendee list for
// manual fallback. Kept as a separate component rather than parameterising the
// admin one — the two auth paths use different services and result types, and
// the divergence isn't worth the abstraction.

import { useEffect, useRef, useState, useCallback } from "react";
import { Html5Qrcode } from "html5-qrcode";
import {
  CheckCircle2, AlertTriangle, XCircle, Camera, CameraOff, Search, Loader2,
} from "lucide-react";
import {
  useCatalystCheckInById, useCatalystCheckInByToken, useCatalystEventAttendees,
} from "../../hooks/useCatalystPortal";
import type { CatalystCheckInResponse } from "../../api/catalystPortal";

const SCANNER_ID = "catalyst-checkin-scanner";
type Result = CatalystCheckInResponse["result"];

const OUTCOME: Record<Result, { cls: string; icon: typeof CheckCircle2; label: string }> = {
  ok:                 { cls: "border-emerald-500 bg-emerald-500/15 text-emerald-300", icon: CheckCircle2,   label: "مقبول — Admit" },
  already_checked_in: { cls: "border-amber-500 bg-amber-500/15 text-amber-300",       icon: AlertTriangle,  label: "Already checked in" },
  not_accepted:       { cls: "border-red-500 bg-red-500/15 text-red-300",             icon: XCircle,        label: "Not accepted" },
  wrong_event:        { cls: "border-red-500 bg-red-500/15 text-red-300",             icon: XCircle,        label: "Wrong meetup" },
  not_found:          { cls: "border-red-500 bg-red-500/15 text-red-300",             icon: XCircle,        label: "Unknown ticket" },
};

function formatTime(iso?: string | null) {
  if (!iso) return "";
  return new Date(iso).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
}

export function CatalystCheckInScanner({ eventId }: { eventId: string }) {
  const scannerRef = useRef<Html5Qrcode | null>(null);
  const lastScanRef = useRef<{ token: string; at: number } | null>(null);

  const [isScanning, setIsScanning] = useState(false);
  const [cameraError, setCameraError] = useState<string | null>(null);
  const [outcome, setOutcome] = useState<CatalystCheckInResponse | null>(null);
  const [manualSearch, setManualSearch] = useState("");

  const { mutate: checkInToken, isPending: isCheckingToken } = useCatalystCheckInByToken();
  const { mutate: checkInId, isPending: isCheckingId } = useCatalystCheckInById();

  // The catalyst portal doesn't have a server-side search on its attendee list,
  // so filter the full event list client-side. Fine at meetup scale (<200).
  const { data: fullList } = useCatalystEventAttendees(eventId, {}, !!eventId);
  const q = manualSearch.trim().toLowerCase();
  const manualResults = q.length >= 2
    ? (fullList ?? []).filter((a) =>
        a.fullName.toLowerCase().includes(q) ||
        a.email.toLowerCase().includes(q) ||
        a.whatsapp.toLowerCase().includes(q),
      ).slice(0, 20)
    : [];

  const submitToken = useCallback(
    (raw: string) => {
      const now = Date.now();
      if (lastScanRef.current && lastScanRef.current.token === raw && now - lastScanRef.current.at < 3000) return;
      lastScanRef.current = { token: raw, at: now };
      checkInToken({ token: raw, eventId }, { onSuccess: setOutcome });
    },
    [checkInToken, eventId],
  );

  useEffect(() => {
    if (!isScanning) return;
    const scanner = new Html5Qrcode(SCANNER_ID, { verbose: false });
    scannerRef.current = scanner;
    scanner
      .start(
        { facingMode: "environment" },
        { fps: 10, qrbox: { width: 240, height: 240 } },
        (decoded) => submitToken(decoded),
        () => undefined,
      )
      .catch(() => {
        setCameraError("Couldn't open the camera. Check browser permissions, or use manual check-in below.");
        setIsScanning(false);
      });
    return () => {
      scanner.stop().then(() => scanner.clear()).catch(() => undefined);
      scannerRef.current = null;
    };
  }, [isScanning, submitToken]);

  const isBusy = isCheckingToken || isCheckingId;
  const view = outcome ? OUTCOME[outcome.result] : null;
  const Icon = view?.icon;

  return (
    <div className="space-y-4">
      {outcome && view && Icon && (
        <div className={`card border-2 p-5 flex items-start gap-4 ${view.cls}`}>
          <Icon size={32} className="shrink-0" />
          <div className="min-w-0">
            <p className="font-bold text-lg leading-tight">{view.label}</p>
            {outcome.attendee && (
              <p className="text-white text-base mt-1 truncate" dir="rtl">{outcome.attendee.fullName}</p>
            )}
            <p className="text-sm opacity-80 mt-0.5">
              {outcome.message}
              {outcome.result === "already_checked_in" && outcome.attendee?.checkedInAt
                ? ` (${formatTime(outcome.attendee.checkedInAt)})`
                : ""}
            </p>
          </div>
        </div>
      )}

      <div className="card p-5">
        <div className="flex items-center justify-between gap-3 mb-4">
          <div>
            <p className="text-white font-semibold text-sm">QR check-in</p>
            <p className="text-brand-muted text-xs mt-0.5">
              Point the camera at the attendee&rsquo;s ticket code.
            </p>
          </div>
          <button
            onClick={() => { setIsScanning((v) => !v); setCameraError(null); }}
            className={isScanning ? "btn-ghost text-sm flex items-center gap-1.5" : "btn-primary text-sm flex items-center gap-1.5"}
          >
            {isScanning ? <><CameraOff size={14} /> Stop</> : <><Camera size={14} /> Start camera</>}
          </button>
        </div>

        <div id={SCANNER_ID} className={`overflow-hidden rounded-xl bg-brand-navy ${isScanning ? "block" : "hidden"}`} />

        {!isScanning && !cameraError && (
          <div className="rounded-xl border border-dashed border-brand-border py-10 text-center text-brand-muted text-sm">
            Camera is off.
          </div>
        )}
        {cameraError && (
          <div className="rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-red-400 text-sm">
            {cameraError}
          </div>
        )}
        {isBusy && (
          <p className="text-brand-muted text-xs mt-3 flex items-center gap-1.5">
            <Loader2 size={12} className="animate-spin" /> Checking in…
          </p>
        )}
      </div>

      <div className="card p-5">
        <p className="text-white font-semibold text-sm mb-1">Manual check-in</p>
        <p className="text-brand-muted text-xs mb-3">
          If the code won&rsquo;t scan, find the attendee by name, email or phone.
        </p>
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-muted" />
          <input
            type="text"
            value={manualSearch}
            onChange={(e) => setManualSearch(e.target.value)}
            placeholder="Search this event's attendees…"
            className="w-full pl-9 pr-3 py-2 text-sm bg-brand-navy border border-brand-border rounded-lg text-white placeholder-brand-muted focus:outline-none focus:border-brand-green transition-colors"
          />
        </div>

        {q.length >= 2 && (
          <div className="mt-3 space-y-2 max-h-64 overflow-y-auto">
            {manualResults.length === 0 && (
              <p className="text-brand-muted text-xs py-2">No matches.</p>
            )}
            {manualResults.map((a) => (
              <div key={a.id} className="flex items-center justify-between gap-3 rounded-lg border border-brand-border px-3 py-2">
                <div className="min-w-0">
                  <p className="text-white text-sm truncate" dir="rtl">{a.fullName}</p>
                  <p className="text-brand-muted text-xs truncate">
                    {a.email} · {a.approvalStatus}
                    {a.checkedInAt ? ` · in at ${formatTime(a.checkedInAt)}` : ""}
                  </p>
                </div>
                <button
                  onClick={() => checkInId(a.id, { onSuccess: setOutcome })}
                  disabled={isBusy || !!a.checkedInAt || a.approvalStatus !== "accepted"}
                  className="btn-primary text-xs shrink-0 disabled:opacity-40"
                  title={
                    a.checkedInAt ? "Already checked in"
                    : a.approvalStatus !== "accepted" ? "Not accepted"
                    : "Check in"
                  }
                >
                  {a.checkedInAt ? "In" : "Check in"}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
